// Registry central das URLs de backend (Cloudflare Pages Functions).
//
// Por que existir: CLAUDE.md manda que toda URL nova seja registrada aqui em
// vez de espalhar strings literais pelas features. Centralizar facilita
// migração coordenada com o backend (mudar uma rota = mudar um lugar).
//
// Como usar:
//   import { API } from '../../core/api.js';
//   const res = await authFetch(API.usersSearch(term));
//
// Endpoints legados (`/api/documents/*`, `/api/admin/*`, etc.) ainda usam
// strings literais nas features que os introduziram — migração incremental.
// Novas rotas devem ser adicionadas aqui.

export const API = {
  /**
   * Busca usuários Mapiare por nome ou email (autocomplete de convite).
   * Backend: functions/api/users/search.js (autenticado, min 2 chars, cap 8).
   */
  usersSearch: (q) => `/api/users/search?q=${encodeURIComponent(q)}`,

  /**
   * Dispara a geração ASSÍNCRONA de um roteiro via IA (Gemini).
   * POST { tripId, prompt, currency } → 202 { accepted }.
   * Backend: functions/api/ai/generate-trip.js — autenticado + allowlist
   * (doc `aiAccess/{uid}`). A chave do Gemini nunca chega no cliente.
   */
  aiGenerateTrip: () => '/api/ai/generate-trip',

  /**
   * Status da geração de roteiro com IA de uma viagem.
   * GET → { status: 'ready'|'generating'|'failed', error }.
   * Backend: functions/api/ai/trip-status.js.
   */
  aiTripStatus: (tripId) => `/api/ai/trip-status?trip=${encodeURIComponent(tripId)}`,

  /**
   * Regenera com IA os ITENS do itinerário de UM dia (síncrono, sem Worker).
   * POST { tripId, day, instruction } → 200 { items:[...] }. NÃO toca hotel,
   * transportes nem carteira — só devolve itens propostos; o cliente aplica.
   * Backend: functions/api/ai/edit-day.js — autenticado + acesso de IA + membro.
   */
  aiEditDay: () => '/api/ai/edit-day',

  /**
   * Exporta um PDF único juntando os documentos anexados à viagem, agrupados
   * por tipo. POST { tripId, sections:[kind...] }.
   * Sucesso: 200 application/pdf (attachment) + headers X-Export-Included /
   * X-Export-Skipped. Volume grande: 413 { async:true }.
   * Backend: functions/api/exports/pdf.js — autenticado + membro da viagem.
   */
  exportPdf: () => '/api/exports/pdf',

  /**
   * Imagem estática (PNG) do Google Static Maps pra rota da viagem, cacheada
   * no R2 e servida same-origin (não tainta o canvas do card). Usada só no
   * estilo "Mapa" do compartilhamento.
   * GET ?pts=lat,lng;lat,lng...&theme=dark|light → image/png.
   * Backend: functions/api/maps/static.js — autenticado; 503 se a chave não
   * for utilizável server-side (cliente cai no traço estilizado).
   */
  /**
   * Exclusão permanente da conta do usuário LOGADO (nunca de outro uid).
   * GET  → { owned, guest, email } — prévia pro texto do modal.
   * DELETE ?confirm=EXCLUIR → { ok:true, tripsDeleted, tripsLeft, ... }.
   * Apaga só os DADOS (Firestore + R2); o usuário do Firebase Auth é apagado
   * pelo cliente logo depois (src/core/auth.js → deleteAccount()).
   * Backend: functions/api/users/delete-account.js — autenticado.
   */
  deleteAccount: (confirm) =>
    `/api/users/delete-account${confirm ? `?confirm=${encodeURIComponent(confirm)}` : ''}`,

  /**
   * Cotações de câmbio (comercial + VET) via API externa, cacheadas no KV.
   * GET ?base=BRL → { base, markupPct, commercial:{...}, vet:{...}, ... }.
   * Backend: functions/api/currency/rates.js — autenticado (só estar logado).
   */
  currencyRates: (base = 'BRL') => `/api/currency/rates?base=${encodeURIComponent(base)}`,

  mapsStatic: (pts, theme = 'dark', labeled = false, ar = 1.5) =>
    `/api/maps/static?theme=${encodeURIComponent(theme)}${labeled ? '&labeled=1' : ''}`
    + `&ar=${encodeURIComponent(ar)}&pts=${encodeURIComponent(pts)}`,
};
